import React from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Row } from 'react-table';
import CircularProgress from '@mui/material/CircularProgress';
import { SxProps, Theme } from '@mui/material/styles';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { GridTableContext, GridContextProps } from '../../contexts';
import { Column } from '../../interfaces';
import { Pagination } from '../pagination.component';
import { DraggableHeader } from './draggable-header.component';
import { EmptyRow } from './empty-row.component';
import { classes, TableRowStyled, TableStyled } from './table.style';

export function GridTable<TEntity extends object = Record<string, unknown>>({
  sx,
  onRowClick,
  rowClassNameByIndex,
  sxRowProps
}: {
  sx?: SxProps;
  rowClassNameByIndex?: Record<string, number[]>;
  onRowClick?: (row: { row: TEntity; index: number }) => void;
  sxRowProps?: (row: { row: TEntity }) => SxProps;
}): JSX.Element {
  const {
    instance: {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      page,
      prepareRow,
      showHeaderTooltip,
      showCellTooltip,
      allColumns
    },
    columnsOrder,
    setColumnsOrder,
    showLoader,
    hidePagination
  } = React.useContext<GridContextProps<TEntity>>(GridTableContext);

  React.useEffect(() => {
    setColumnsOrder(allColumns);
  }, [allColumns, setColumnsOrder]);

  const visibleColumnsOrder = React.useMemo(
    () => columnsOrder.filter((column) => column.isVisible),
    [columnsOrder]
  );

  const getRowClassName = (index: number): string => {
    if (!rowClassNameByIndex) return '';

    const classNamesAndIndexes = Object.entries(rowClassNameByIndex);
    const classNamesWithIndex = classNamesAndIndexes
      .filter(([, indexes]) => indexes.includes(index))
      .map(([className]) => className);

    return classNamesWithIndex.join(' ');
  };

  const clickleableRowStyle = onRowClick ? { cursor: 'pointer' } : {};

  const rowOnclick = (row: Row<TEntity>): void => {
    if (onRowClick) {
      onRowClick({ row: row.original, index: row.index });
    }
  };
  const sxRow = (row: Row<TEntity>): SxProps => {
    if (sxRowProps) {
      return sxRowProps({ row: row.original });
    }
    return {};
  };
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = visibleColumnsOrder.findIndex((col) => col.id === active.id);
      const newIndex = visibleColumnsOrder.findIndex((col) => col.id === over?.id);

      const reorderedColumns = arrayMove(visibleColumnsOrder, oldIndex, newIndex);

      setColumnsOrder((prevColumnsOrder) => {
        const updatedOrder = [...prevColumnsOrder];
        reorderedColumns.forEach((column, idx) => {
          const realIndex = updatedOrder.findIndex((col) => col.id === column.id);
          if (realIndex !== idx) {
            const [removed] = updatedOrder.splice(realIndex, 1);
            updatedOrder.splice(idx, 0, removed);
          }
        });
        return updatedOrder;
      });
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <TableStyled {...getTableProps()} sx={sx} className={classes.root}>
        <TableHead className={classes.head}>
          {headerGroups.map((headerGroup) => {
            const { key, ...restProps } = headerGroup.getHeaderGroupProps();
            return (
              <TableRow {...restProps} key={key}>
                {visibleColumnsOrder.map((column) => (
                  <TableCell {...column.getHeaderProps()} key={column.id} className={classes.cell}>
                    <DraggableHeader
                      column={column}
                      id={column.id}
                      showHeaderTooltip={showHeaderTooltip}
                    />
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableHead>

        <TableBody {...getTableBodyProps()}>
          {showLoader ? (
            <EmptyRow>
              <CircularProgress />
            </EmptyRow>
          ) : page.length < 1 ? (
            <EmptyRow>
              <span>No data found</span>
            </EmptyRow>
          ) : (
            page.map((row, index) => {
              prepareRow(row);
              return (
                <TableRowStyled
                  onClick={() => rowOnclick(row)}
                  sx={{ ...clickleableRowStyle, ...sxRow(row) }}
                  key={row.getRowProps().key}
                  className={getRowClassName(index)}
                >
                  {visibleColumnsOrder.map((column) => {
                    const cell = row.cells.find((c) => c.column.id === column.id);
                    if (!cell) return null;

                    const { key, ...restProps } = cell.getCellProps();
                    const disableTooltipCell = (cell.column as Column<TEntity>).disableTooltipCell;
                    const cellSx = (cell.column as Column<TEntity>).cellSx;
                    const dynamicCellSx = (cell.column as Column<TEntity>).dynamicCellSx;
                    const computedSx =
                      typeof dynamicCellSx === 'function'
                        ? { ...cellSx, ...dynamicCellSx(cell.row.original) }
                        : cellSx;
                    if (showCellTooltip && !disableTooltipCell)
                      return (
                        <Tooltip title={cell.value} enterDelay={250} key={key}>
                          <TableCell
                            {...restProps}
                            className={classes.cell}
                            sx={computedSx as SxProps<Theme>}
                          >
                            {cell.render('Cell')}
                          </TableCell>
                        </Tooltip>
                      );
                    return (
                      <TableCell
                        {...restProps}
                        className={classes.cell}
                        sx={computedSx as SxProps<Theme>}
                        key={key}
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    );
                  })}
                </TableRowStyled>
              );
            })
          )}
        </TableBody>
      </TableStyled>
      {!hidePagination && <Pagination />}
    </DndContext>
  );
}
